/*
 * Icon management tools for toggles/clicks/hovers.
 */
import classNames from 'classnames';
import CoreComponent from './CoreComponent';

class Base extends CoreComponent {
    constructor(props) {
        super(props);
        this._settings = {
            className: this.props.className || 'far',
        };
    }

    get settings() {
        return Object.assign({}, this._settings || {}, this.state);
    }

    set settings(data) {
        this._settings = Object.assign(this._settings || {}, data || {});
    }

    static shallowCompare(a, b) {
        return Object.keys(a).concat(Object.keys(b)).every(k => a[k] === b[k])
    }

    static getDerivedStateFromProps(props = {}, state = {}) {
        return !Base.shallowCompare(props, state) ? { ...state, ...props } : null
    }

    render() {
        let { children, className, count, ...props } = this.props;

        return (
            <i
                className={
                    classNames('IconBase', { IconBadge: count >= 1 }, (className || this.settings.className), (this.settings[this.settings.type] || this.settings.type))
                }
                data-count={count}
                {...(this.cleanObject(props, ['type']))}
            >{children}</i>
        );
    }
}

class Toggle extends Base {
    constructor(props) {
        super(props);
        this.state = {
            toggled: ('toggled' in this.props) ? this.props.toggled : false,
        };
        this.settings = {
            className: this.props.className || 'far',
            true: this.props.on || 'fa-check-square',
            false: this.props.off || 'fa-square',
        };
    }

    static getDerivedStateFromProps(props = {}, state = {}) {
        return props.shallow === 0 ? null : (!Base.shallowCompare(props, state) ? { ...state, ...props } : null);
    }

    render() {
        let { children, onClick, className, style, count, ...props } = this.props;

        return (
            <i
                className={
                    classNames('IconToggle', { IconBadge: count >= 1 }, (className || this.settings.className), this.settings[this.isToggled('toggled')])
                }
                style={
                    { cursor: 'pointer', ...(style || {}) }
                }
                onClick={
                    () => {
                        this.toggleStates('toggled').then(() => {
                            this.setTimeout(() => {
                                (onClick || (() => { }))(this.isToggled('toggled'));
                            }, 1);
                        });
                    }
                }
                data-count={count}
                {...(this.cleanObject(props, ['on', 'off', 'toggled']))}
            >{children}</i>
        );
    }
}

class Click extends Base {
    constructor(props) {
        super(props);
        this.settings = {
            className: this.props.className || 'far',
            type: this.props.type || 'fa-smile',
        };
    }

    render() {
        let { children, onClick, className, style, count, ...props } = this.props;

        return (
            <i
                className={
                    classNames('IconClick', { IconBadge: count >= 1 }, (className || this.settings.className), this.settings.type)
                }
                style={
                    { cursor: 'pointer', ...(style || {}) }
                }
                onClick={
                    () => {
                        this.setTimeout(() => {
                            (onClick || (() => { }))(this.refs.current);
                        }, 1)
                    }
                }
                data-count={count}
                {...(this.cleanObject(props, ['type']))}
            >{children}</i>
        );
    }
}

class Hover extends Base {
    constructor(props) {
        super(props);
        this.state = {
            hover: ('hovered' in this.props) ? this.props.hovered : false,
        };
        this.settings = {
            className: this.props.className || 'far',
            true: this.props.on || 'fa-check-square',
            false: this.props.off || 'fa-square',
        };
    }

    hover(event, hover = true) {
        this.setStatePrev({ hover }).then(() => {
            this.setTimeout(() => {
                (this.props.onHover || (() => { }))(this.isToggled('hover'));
            }, 1);
        });
    }

    render() {
        let { children, className, style, count, ...props } = this.props;

        return (
            <i
                className={
                    classNames('IconHover', { IconBadge: count >= 1 }, (className || this.settings.className), this.settings[this.isToggled('hover')])
                }
                style={
                    { cursor: 'pointer', ...(style || {}) }
                }
                onMouseOver={(event) => this.hover(event, true)}
                onMouseOut={(event) => this.hover(event, false)}
                data-count={count}
                {...(this.cleanObject(props, ['on', 'off']))}
            >{children}</i>
        );
    }
}

export {
    Base,
    Toggle,
    Click,
    Hover,
}
